import { ChangeEvent, FormEvent, useState, useEffect } from 'react';
import { SupportTicketData, MT5AccountUser } from '../../utils/types';
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
} from '../../constants/supportOptions';
import SupportInfo from './SupportInfo';
import IssueDescription from './IssueDescription';
import TicketInformation from './TicketInformation';
import Attachments from './Attachments';
import { mt5API } from '../../services/api';

interface AddSupportTicketFormProps {
  onSubmit?: (data: SupportTicketData) => void;
}

const AddSupportTicketForm = ({ onSubmit }: AddSupportTicketFormProps) => {
  const [formData, setFormData] = useState<SupportTicketData>({
    subject: '',
    category: 'Trading Issues',
    priority: 'Medium',
    issueDescription: '',
    accountNumber: '',
    email: '',
    attachments: [],
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof SupportTicketData, string>>
  >({});
  const [fileError, setFileError] = useState<string>('');
  const [mt5Accounts, setMt5Accounts] = useState<MT5AccountUser[]>([]);
  const [loadingMt5Accounts, setLoadingMt5Accounts] = useState(false);

  useEffect(() => {
    const fetchMt5Accounts = async () => {
      setLoadingMt5Accounts(true);
      try {
        const response = await mt5API.getUsers();
        const accountsData = response?.data || response;

        if (accountsData && Array.isArray(accountsData)) {
          setMt5Accounts(accountsData);
        }
      } catch (error) {
        console.error('Error fetching MT5 accounts:', error);
      } finally {
        setLoadingMt5Accounts(false);
      }
    };

    fetchMt5Accounts();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof SupportTicketData, string>> = {};
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length < 20 || formData.subject.length > 50) {
      newErrors.subject = 'Subject must be between 20 and 50 characters';
    }

    if (!formData.issueDescription.trim()) {
      newErrors.issueDescription = 'Description is required';
    } else if (formData.issueDescription.length < 20) {
      newErrors.issueDescription = 'Description must be at least 20 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof SupportTicketData,
    value: string | File[]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentFiles = formData.attachments || [];
    if (currentFiles.length + files.length > 2) {
      setFileError('You can only upload a maximum of 2 files.');
      return;
    }

    setFileError('');
    handleInputChange('attachments', [...currentFiles, ...files]);
  };

  const removeFile = (index: number) => {
    const currentFiles = formData.attachments || [];
    const newFiles = currentFiles.filter((_, i) => i !== index);
    setFileError('');
    handleInputChange('attachments', newFiles);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 overflow-x-hidden"
      data-drawer-form="true"
    >
      <TicketInformation
        subject={formData.subject}
        category={formData.category}
        priority={formData.priority}
        email={formData.email}
        accountNumber={formData.accountNumber}
        onSubjectChange={value => handleInputChange('subject', value)}
        onCategoryChange={value => handleInputChange('category', value)}
        onPriorityChange={value => handleInputChange('priority', value)}
        onEmailChange={value => handleInputChange('email', value)}
        onAccountNumberChange={value =>
          handleInputChange('accountNumber', value)
        }
        errors={errors}
        categories={SUPPORT_CATEGORIES}
        priorities={SUPPORT_PRIORITIES}
        mt5Accounts={mt5Accounts}
        showMt5Dropdown={true}
        loadingMt5Accounts={loadingMt5Accounts}
      />

      <IssueDescription
        value={formData.issueDescription}
        onChange={value => handleInputChange('issueDescription', value)}
        error={errors.issueDescription}
      />

      <Attachments
        attachments={formData.attachments || []}
        onFileChange={handleFileChange}
        onRemoveFile={removeFile}
        fileError={fileError}
      />

      <SupportInfo
        items={[
          'Our support team will review your ticket within 24 hours',
          "You'll receive updates via email as we work on your issue",
          'You can check your ticket status in the Support Center',
          'Average response time: 2-4 hours for urgent issues',
        ]}
      />
    </form>
  );
};

export default AddSupportTicketForm;
